/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementType,
  ForwardedRef,
  forwardRef,
  ReactNode,
  Ref,
} from 'react';
import { useInputGroupClassName } from '../../legacy/InputGroup';
import { FSMaskProp } from '../../types';
import { identifyComponent } from '../../util';
import { ButtonBase } from '../ButtonBase';
import type { IconName } from '../Icon';
import { Icon } from '../Icon';
import { Text } from '../Text';
import css from './index.module.scss';

type ButtonSize = 'x-small' | 'small' | 'medium' | 'large';
type ButtonVariant =
  | 'fill-primary'
  | 'fill-secondary'
  | 'outline'
  | 'minimal'
  | 'fill-builder'
  | 'underline-builder'
  | 'fill-destructive'
  | 'minimal-destructive'
  | 'fill-intelligence'
  | 'outline-intelligence'
  | 'outline-intelligence-gradient'
  | 'fill-brand';

const iconSizeMap: { [size in ButtonSize]: React.ComponentProps<typeof Icon>['size'] } = {
  'x-small': 16,
  small: 16,
  medium: 16,
  large: 20,
};

// TODO: update type of value to match margin scale
const iconMarginMap: { [size in ButtonSize]: number } = {
  'x-small': 8,
  small: 8,
  medium: 12,
  large: 12,
};

const typeMap: Map<ButtonSize, ComponentProps<typeof Text>['appearance']> = new Map([
  ['x-small', 'bodySmallBold'],
  ['small', 'bodySmallBold'],
  ['medium', 'bodyMediumBold'],
  ['large', 'bodyLargeBold'],
]);

export interface ButtonCustomContentProps<T extends ElementType = typeof ButtonBase> {
  /**
   * For linking labels to button elements within forms via htmlFor. Should not be used to apply
   * styles
   */
  id?: string;
  /**
   * For element identity purpose. Default to 'button'
   */
  testId?: string;
  /**
   * Size of the button. Default is 'medium'
   */
  size?: ButtonSize;
  /**
   * Type of button. Default is 'outline'
   */
  variant?: ButtonVariant;
  /**
   * If true, the button will not be able to be selected or clicked.
   */
  disabled?: boolean;
  /**
   * Fires when the button is clicked or activated via a keypress.
   */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Expand the button to the width of its nearest ancestor with an explicit width.
   */
  fullWidth?: boolean;
  /**
   * By default, button contents are positioned using the Flex property `space-between`.
   * This prop also supports 'center'. (TODO: add more options as necessary)
   */
  justifyContent?: 'space-between' | 'center';
  /**
   * Optional className for legacy support. Should not be used for custom styling. Use ButtonBase instead.
   */
  className?: string;
  /**
   * Element used for rendering the button. The default is `button` but
   * sometimes we need to render an element that _looks_ like a button but is
   * semantically a link.
   *
   * Automatically set to <a> when href attribute is present.
   *
   * Be sure to follow accessibility guidelines when using tags other than button.
   */
  tag?: T;
  fsMask?: FSMaskProp;
  children?: ReactNode;
  /**
   * URL. Will force tag to <a>, turning the button into a link.
   */
  href?: string;
}

export interface ButtonProps<T extends ElementType = typeof ButtonCustomContent>
  extends ButtonCustomContentProps<T> {
  /**
   * The icon to use. Appears to the left of the button text.
   */
  iconLeft?: IconName | null;
  /**
   * The icon to use. Appears to the right of the button text.
   */
  iconRight?: IconName | null;
  children?: ReactNode;
}

function ButtonInner<T extends ElementType>(
  {
    testId = 'button',
    size = 'medium',
    variant = 'outline',
    disabled = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    className,
    justifyContent = 'space-between',
    children,
    fsMask,
    tag,
    href,
    ...rest
  }: ButtonProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps<T>>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const iconMargin = iconMarginMap[size];
  const iconSize = iconSizeMap[size];

  return (
    <ButtonCustomContentWithRef
      data-component="ButtonInner"
      testId={testId}
      size={size}
      variant={variant}
      disabled={disabled}
      fullWidth={fullWidth}
      className={className}
      justifyContent={justifyContent}
      fsMask={fsMask}
      tag={tag}
      href={href}
      ref={ref}
      {...rest}
    >
      <>
        {iconLeft && (
          <div style={{ marginRight: iconMargin }}>
            <Icon name={iconLeft} size={iconSize} />
          </div>
        )}
        {children && (
          <Text
            tag="span"
            appearance={typeMap.get(size) || 'bodyMediumBold'}
            color="inherit"
            noWrap
          >
            {children}
          </Text>
        )}
        {variant === 'underline-builder' && <span className={css.underlineLine} />}
        {iconRight && (
          <div style={{ marginLeft: iconMargin }}>
            <Icon name={iconRight} size={iconSize} />
          </div>
        )}
      </>
    </ButtonCustomContentWithRef>
  );
}

function ButtonCustomContentInner<T extends ElementType>(
  {
    testId = 'button',
    size = 'medium',
    variant = 'outline',
    disabled = false,
    fullWidth = false,
    className,
    justifyContent = 'space-between',
    children,
    fsMask,
    tag,
    href,
    ...rest
  }: ButtonCustomContentProps<T> &
    Omit<ComponentPropsWithoutRef<T>, keyof ButtonCustomContentProps<T>>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const inputGroupItemClassName = useInputGroupClassName();

  return (
    <ButtonBase
      data-component="ButtonInner"
      testId={testId}
      ref={ref}
      className={classNames(
        css.button,
        css[size],
        css[variant],
        {
          [css.fullWidth]: fullWidth,
          [css.centered]: justifyContent === 'center',
          [css.disabled]: disabled,
        },
        inputGroupItemClassName,
        className,
      )}
      disabled={disabled}
      fsMask={fsMask}
      component={href ? 'a' : tag}
      href={href}
      {...rest}
      {...identifyComponent('button', rest)}
    >
      {children}
    </ButtonBase>
  );
}

const ButtonWithRef = forwardRef(ButtonInner);
const ButtonCustomContentWithRef = forwardRef(ButtonCustomContentInner);

type ButtonWithRefProps<T extends ElementType> = ButtonProps<T> & {
  /**
   * Optional ref.
   *
   * Note: ideally we would use `React.forwardRef()` to forward the ref rather than an
   * `innerRef` prop, but `Button` uses a generic props type which is not
   * supported by `React.forwardRef()`.
   */
  innerRef?: Ref<HTMLButtonElement>;
};

type ButtonCustonContentWithRefProps<T extends ElementType> = ButtonCustomContentProps<T> & {
  /**
   * Optional ref.
   *
   * Note: ideally we would use `React.forwardRef()` to forward the ref rather than an
   * `innerRef` prop, but `Button` uses a generic props type which is not
   * supported by `React.forwardRef()`.
   */
  innerRef?: Ref<HTMLButtonElement>;
};

export function Button<T extends ElementType = typeof ButtonCustomContent>({
  innerRef,
  ...props
}: ButtonWithRefProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps<T>>) {
  return <ButtonWithRef data-component="Button" ref={innerRef} {...props} />;
}

export function ButtonCustomContent<T extends ElementType = typeof ButtonBase>({
  innerRef,
  ...props
}: ButtonCustonContentWithRefProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof ButtonCustomContentProps<T>>) {
  return <ButtonCustomContentWithRef data-component="Button" ref={innerRef} {...props} />;
}
